"use client";

import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import type { WarehouseLocationRow } from "../api/client";
import { shopInventoryApi } from "../api/client";
import { useWarehouseLocationsQuery } from "../api/queries";

type Props = {
  shopId: string;
  branchId: string;
  value: string;
  onValueChange: (locationId: string) => void;
  placeholder?: string;
  className?: string;
  clearable?: boolean;
  /** ກອງແຕ່ default receive */
  defaultReceiveOnly?: boolean;
};

export function ShopWarehouseLocationPicker({
  shopId,
  branchId,
  value,
  onValueChange,
  placeholder = "ເລືອກຕຳແໜ່ງ",
  className,
  clearable = true,
  defaultReceiveOnly = false,
}: Props) {
  const locationsQ = useWarehouseLocationsQuery(shopId, branchId);
  const items = (locationsQ.data?.items ?? []).filter(
    (l) => !defaultReceiveOnly || l.isDefaultReceive,
  );

  return (
    <InfiniteCombobox<WarehouseLocationRow>
      queryKey={["shops", shopId, "location-picker", branchId]}
      queryFn={async () => ({ items, nextPage: null })}
      preloadQueryFn={async (id) => items.find((l) => l.id === id) ?? null}
      getLabel={(item) =>
        item.isDefaultReceive
          ? `${item.code} (ຮັບເຂົ້າ default)`
          : item.code
      }
      getValue={(item) => item.id}
      value={value}
      onValueChange={onValueChange}
      placeholder={placeholder}
      className={className}
      clearable={clearable}
    />
  );
}

export function ShopWarehouseLocationPickerRemote({
  shopId,
  branchId,
  value,
  onValueChange,
  placeholder = "ເລືອກຕຳແໜ່ງ",
  className,
  clearable = true,
}: Omit<Props, "defaultReceiveOnly">) {
  return (
    <InfiniteCombobox<{ id: string; name: string }>
      queryKey={["shops", shopId, "inventory", "location-lookup", branchId]}
      queryFn={async ({ search: q, pageParam }) => {
        const res = await shopInventoryApi.listWarehouseLocations(shopId, {
          branchId,
        });
        const filtered = res.items.filter(
          (l) =>
            !q ||
            l.code.toLowerCase().includes(q.toLowerCase()) ||
            (l.name?.toLowerCase().includes(q.toLowerCase()) ?? false),
        );
        const page = pageParam ?? 1;
        const limit = 20;
        const start = (page - 1) * limit;
        const slice = filtered.slice(start, start + limit);
        return {
          items: slice.map((l) => ({
            id: l.id,
            name: l.isDefaultReceive ? `${l.code} (default)` : l.code,
          })),
          nextPage: start + limit < filtered.length ? page + 1 : null,
        };
      }}
      preloadQueryFn={async (id) => {
        const res = await shopInventoryApi.listWarehouseLocations(shopId, {
          branchId,
        });
        const loc = res.items.find((l) => l.id === id);
        return loc
          ? {
              id: loc.id,
              name: loc.isDefaultReceive ? `${loc.code} (default)` : loc.code,
            }
          : null;
      }}
      getLabel={(item) => item.name}
      getValue={(item) => item.id}
      value={value}
      onValueChange={onValueChange}
      placeholder={placeholder}
      className={className}
      clearable={clearable}
    />
  );
}
